import { useEffect, useState, forwardRef } from "react";
import type { HTMLAttributes } from "react";
// ui
import {
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
// ★U8(cmd-po_1109): sip-stepper共通部品
import { SipStepper } from "../common/SipStepper";
// ★cmd-po_1205 (T4-a): frame→PoModalShell / footer→PoButton へ移行
import { PoModalShell } from "../common/PoModalShell";
import { PoButton } from "../common/PoButton";

// jotai
import { useAtomValue } from "jotai";
import { AppAtoms } from "../../lib/store";

/* ─── Autocomplete 選択肢 Paper: 注記を末尾に付加（cmd-po_1133）─── */
/* MUI Paperを使わず div で代替（retirement grep 対象=0件を維持） */
const CACOptionsPaper = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(
  function CACOptionsPaper({ children }, ref) {
    return (
      <div
        ref={ref}
        data-testid="change-account-options"
        style={{
          background: "white",
          border: "1px solid var(--color-border, #e5e5e0)",
          borderRadius: "var(--radius-sm, 6px)",
          boxShadow: "var(--shadow-elevated, 0 8px 32px rgba(30,58,95,.12))",
          overflow: "hidden",
        }}
      >
        {children}
        {/* 注記: ※部分一致（mock .apk-note） */}
        {/* padding: var(--sp-1) var(--sp-3) var(--sp-2) = 4px 12px 8px (mock L29) */}
        <div
          data-testid="change-account-option-note"
          style={{
            padding: "var(--sp-1, 4px) var(--sp-3, 12px) var(--sp-2, 8px)",
            fontSize: "var(--fs-10, 10px)",
            color: "var(--color-sumi-muted, #9ca3af)",
          }}
        >
          ※部分一致
        </div>
      </div>
    );
  }
);

/* ─── ラジオ意匠 sx 定数（components.css L94-99 機械転写） ─── */
const rdoBaseSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  border: "1.5px solid var(--color-border, #e5e5e0)",
  borderRadius: "50%",
  bgcolor: "white",
  flexShrink: 0,
};
const rdoOnSx = {
  ...rdoBaseSx,
  border: "1.5px solid var(--color-ai, #1e3a5f)",
};
const rdoDotSx = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  bgcolor: "var(--color-ai, #1e3a5f)",
};
/* po-req（components.css L67 ／ FollowCallModal.tsx L54 先例） */
const reqSx = {
  fontSize: "var(--fs-10, 10px)",
  fontWeight: 600,
  color: "var(--color-kohaku-text, #b45309)",
  ml: 0.5,
};

interface ChangeAccountCollaborateModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  changeAccount: 0 | 1;
  changeAccountID: number | null;
  setChangeAccount: (changeAccount: 0 | 1) => void;
  setChangeAccountID: (changeAccountID: number | null) => void;
  accountCollaborateCompanySaleList: {
    sale_company_id: number;
    sale_company_name: string;
    id: number;
    account_name: string;
  }[];
  title: string;
  getFormFunction: () => void;
  accountColumn: string;
  /** ステッパー段ラベル（登録系5箇所に付与・複製3箇所は未指定）— cmd-po_1133 */
  steps?: string[];
}

export function ChangeAccountCollaborateModal(
  props: ChangeAccountCollaborateModalProps
) {
  const {
    onModal,
    swCloseModal,
    changeAccount,
    changeAccountID,
    setChangeAccount,
    setChangeAccountID,
    accountCollaborateCompanySaleList,
    title,
    getFormFunction,
    accountColumn,
    steps,
  } = props;

  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);

  const [autocompleteInputList, setAutocompleteInputList] = useState<string[]>(
    []
  );
  const [autocompleteInput, setAutocompleteInput] = useState<string>("");

  /* 候補リスト生成（accountCollaborateCompanySaleList 変化時） */
  useEffect(() => {
    setAutocompleteInputList(
      accountCollaborateCompanySaleList.map(
        (input) => input.sale_company_name + " / " + input.account_name
      )
    );
  }, [accountCollaborateCompanySaleList]);

  /* 変更モード変化時の changeAccountID 解決（既存ロジック維持） */
  useEffect(() => {
    let resolvedId: number | null = null;
    if (changeAccount === 0) {
      resolvedId = null;
      setAutocompleteInput("");
    } else {
      accountCollaborateCompanySaleList.map((input) => {
        if (
          autocompleteInput ===
          input.sale_company_name + " / " + input.account_name
        ) {
          resolvedId = input.id;
        }
      });
    }
    setChangeAccountID(resolvedId);
  }, [onModal, changeAccount, autocompleteInput]);

  /* ★cmd-po_1205 (T4-a): footer を PoModalShell の footer スロットへ分離 */
  const footerNode = (
    <>
      {/* DOM順: 閉じる→次へ（cmd-po_1133 AC4） */}
      <PoButton
        variant="secondary"
        data-testid="change-account-close-button"
        onClick={swCloseModal}
      >
        閉じる
      </PoButton>
      <PoButton
        variant="primary"
        data-testid="change-account-next-button"
        disabled={changeAccount === 1 && changeAccountID === null}
        onClick={getFormFunction}
      >
        次へ
      </PoButton>
    </>
  );

  return (
    <PoModalShell
      open={onModal}
      title={title}
      onClose={swCloseModal}
      widthPx={680}
      testidPrefix="change-account"
      frameTestId="change-account-modal"
      beforeBody={steps && <SipStepper steps={steps} activeIndex={0} />}
      bodySx={{ overflow: "visible", overflowY: "visible" }}
      footer={footerNode}
    >
          {/* フィールド1: 変更モード選択 — .po-field L65 */}
          <Box
            data-testid="change-account-field-mode"
            sx={{ display: "flex", flexDirection: "column", gap: "var(--sp-1, 4px)" }}
          >
            {/* .po-label L66 */}
            <Box
              component="span"
              data-testid="change-account-label-mode"
              sx={{
                fontSize: "var(--fs-12, 12px)",
                fontWeight: 600,
                color: "var(--color-sumi, #374151)",
                display: "flex",
                alignItems: "center",
                gap: "var(--sp-2, 8px)",
              }}
            >
              {accountColumn}の変更
            </Box>

            {/* ラジオ群 — mock .fm-radios（クラス名は持ち込まない）*/}
            <Box
              role="radiogroup"
              aria-label={`${accountColumn}の変更`}
              data-testid="change-account-select"
              sx={{
                display: "flex",
                gap: "var(--sp-4, 16px)",
                py: "var(--sp-1, 4px)",
              }}
            >
              {([0, 1] as const).map((v) => (
                <Box
                  key={v}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={changeAccount === v}
                  data-testid={`change-account-radio-${v}`}
                  onClick={() => setChangeAccount(v)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "var(--sp-2, 8px)",
                    fontSize: "var(--fs-13, 13px)",
                    color: "var(--color-sumi, #374151)",
                    bgcolor: "transparent",
                    border: "none",
                    p: 0,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {/* .rdo / .rdo.on / .rdo-dot — components.css L94-99 */}
                  <Box
                    component="span"
                    sx={changeAccount === v ? rdoOnSx : rdoBaseSx}
                  >
                    {changeAccount === v && (
                      <Box component="span" sx={rdoDotSx} />
                    )}
                  </Box>
                  {/* ラジオ文言: mock DOM契約表どおり（accountColumn接頭辞なし・cmd-po_1133 rw1） */}
                  {v === 0 ? "変更しない" : "変更する"}
                </Box>
              ))}
            </Box>
          </Box>

          {/* フィールド2: アカウント Autocomplete — .po-field L65 */}
          <Box
            data-testid="change-account-field-account"
            sx={{ display: "flex", flexDirection: "column", gap: "var(--sp-1, 4px)" }}
          >
            {/* .po-label + .po-req L66-67 */}
            <Box
              component="span"
              data-testid="change-account-label-account"
              sx={{
                fontSize: "var(--fs-12, 12px)",
                fontWeight: 600,
                color: "var(--color-sumi, #374151)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {accountColumn}
              <Box component="span" data-testid="change-account-req" sx={reqSx}>
                ※必須
              </Box>
            </Box>

            {/* Autocomplete — mock .apk-wrap (position:relative = L25・openOnFocus=click→open) */}
            <Autocomplete
              data-testid="change-account-autocomplete"
              sx={{ position: "relative" }}
              openOnFocus
              forcePopupIcon={false}
              disableClearable
              disabled={changeAccount === 0}
              options={autocompleteInputList}
              value={autocompleteInput}
              onChange={(_event, newValue) => {
                if (newValue) setAutocompleteInput(newValue);
              }}
              /* 選択肢 Paper（注記付き） */
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              PaperComponent={CACOptionsPaper as any}
              /* 選択肢意匠 — .apk-item/.apk-item.hot */
              slotProps={{
                listbox: {
                  sx: {
                    "& .MuiAutocomplete-option": {
                      px: "var(--sp-3, 12px)",
                      py: "var(--sp-2, 8px)",
                      fontSize: "var(--fs-13, 13px)",
                      color: "var(--color-sumi, #374151)",
                    },
                    "& .MuiAutocomplete-option.Mui-focused": {
                      bgcolor: "var(--color-ai-pale, #e8eef4)",
                      color: "var(--color-ai, #1e3a5f)",
                      fontWeight: 600,
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  value={autocompleteInput}
                  sx={{
                    /* fieldset 非表示 → .MuiInputBase-root 直接ボーダー化 */
                    "& .MuiOutlinedInput-notchedOutline": { display: "none" },
                    "& .MuiInputBase-root": {
                      height: "var(--control-h, 36px)",
                      border: "1px solid var(--color-border, #e5e5e0)",
                      borderRadius: "var(--radius-sm, 6px)",
                      fontSize: "var(--fs-13, 13px)",
                      color: "var(--color-sumi, #374151)",
                      bgcolor: "white",
                      px: "var(--sp-3, 12px)",
                      py: 0,
                      fontFamily: "var(--font-sans, inherit)",
                      boxSizing: "border-box",
                    },
                    /* .po-input.disabled — components.css L77 */
                    "& .MuiInputBase-root.Mui-disabled": {
                      bgcolor: "var(--color-washi, #f8f7f5)",
                      color: "var(--color-sumi-muted, #9ca3af)",
                      WebkitTextFillColor: "var(--color-sumi-muted, #9ca3af)",
                    },
                  }}
                />
              )}
            />
          </Box>
    </PoModalShell>
  );
}
